import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import helpers from './helpers'
import dayjsPlugin from './plugins/dayjs'
import VueResource from 'vue-resource'
import VueSwal from 'vue-swal'
import * as Sentry from '@sentry/vue'
import {version} from '../package'
import VueSocketIO from 'vue-socket.io'
import SocketIO from 'socket.io-client'
import { entregadoresSocketUrl, getActiveApiUrl, homologApiUrl } from '@/config/endpoints'
import { stopNativeTracking, updateNativeTrackingAuth } from '@/services/nativeTracking'
import { clearPedidosCache } from '@/services/pedidosCache'

import { mockGeolocation } from './utils/mockGeo';
if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_MOCK_GEO === 'true') {
  mockGeolocation();
}

import 'leaflet/dist/leaflet.css';
import 'bootstrap/dist/css/bootstrap.min.css'
import '@fortawesome/fontawesome-free/css/all.min.css'
import './assets/main.css'
import 'animate.css/animate.css'
import './registerServiceWorker'

import { playNotificationAudio, pauseNotificationAudio } from '@/services/notificationAudio';
import { createAuthInterceptor } from '@/services/authInterceptor';
import { createAuthSession } from '@/services/authSession';
let handlingUnauthorized = false;

Vue.use(VueSwal);
Vue.use(helpers);
Vue.use(dayjsPlugin);
Vue.use(VueResource);

const socketConnection = SocketIO(entregadoresSocketUrl, {
  transports: ['websocket', 'polling'],
  withCredentials: true,
  autoConnect: false,
  auth: { token: localStorage.getItem('x_auth') || null }
});

Vue.use(new VueSocketIO({ debug: false, connection: socketConnection }));

function syncSocketToken(token) {
  const changed = socketConnection.auth.token !== token;
  socketConnection.auth = { token };
  // A nova autenticacao e enviada no handshake da conexao.
  if (changed) socketConnection.disconnect();
  if (!socketConnection.connected) socketConnection.connect();
}

const auth = createAuthSession({
  http: Vue.http,
  storage: localStorage,
  authUrl: () => `${getActiveApiUrl()}/auth/v2/me`,
  onTokenRenewed: token => {
    syncSocketToken(token);
    return updateNativeTrackingAuth(token).catch(error => {
      console.error('Falha ao atualizar autenticacao do rastreamento nativo', error);
    });
  },
  onUnauthorized: () => {
    if (handlingUnauthorized) return;
    handlingUnauthorized = true;
    socketConnection.disconnect();
    socketConnection.auth = { token: null };
    stopNativeTracking().catch(error => console.log('stopNativeTracking', error));
    localStorage.removeItem('key');
    localStorage.removeItem('x_auth');
    localStorage.removeItem('appData');
    clearPedidosCache();

    const swal = Vue.prototype.$swal;
    const redirectToLogin = () => {
      if (router.currentRoute.name !== 'Login') {
        router.replace('/');
      }
    };
    const finishUnauthorizedHandling = () => {
      handlingUnauthorized = false;
    };

    redirectToLogin();

    if (typeof swal === 'function') {
      swal('Por favor faça o login novamente.').then(finishUnauthorizedHandling);
    } else {
      finishUnauthorizedHandling();
    }
  }
});

Vue.prototype.$auth = auth;
Vue.http.interceptors.push(createAuthInterceptor({ http: Vue.http, auth }));

if (typeof localStorage !== "undefined") {
  const environment = process.env.VUE_APP_ENVIRONMENT || 'local';

  const activeApiUrl = getActiveApiUrl();
  const base_server = `${activeApiUrl}/`;
  const isHomolog = activeApiUrl === homologApiUrl;

  Date.prototype.toDateInputValue = (function() {
    let local = new Date(this);
    local.setMinutes(this.getMinutes() - this.getTimezoneOffset());
    return local.toJSON().slice(0,10);
  });

  Vue.mixin({
    data() {
      return {
        version,
        base_server,
        isAndroid: /(android)/i.test(navigator.userAgent),
        isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent),
        ios_platform: (window.navigator.standalone === true),
        isHomolog
      }
    },

    methods: {
      playNotification() {
        return playNotificationAudio();
      },

      pauseNotification() {
        pauseNotificationAudio();
      }
    }
  });

  Vue.filter('phone', (phone) => {
    if (!phone) {
      return phone;
    }

    const digits = String(phone).replace(/\D/g, '');
    const possuiDdiBrasil = digits.startsWith('55') && [12, 13].includes(digits.length);
    const numero = possuiDdiBrasil ? digits.slice(2) : digits;
    const prefixo = possuiDdiBrasil ? '+55 ' : '';

    if (numero.length === 10) {
      return prefixo + numero.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    if (numero.length === 11) {
      return prefixo + numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    return phone;
  });

  Vue.filter('kilometer', function (value) {
    if (value !== null || value !== undefined) {
      const formated = parseFloat(value / 1000).toFixed(1);
      return formated + ' km';
    }
  });

  Vue.filter('valor', function (value) {
    if (value === undefined || value === null) {
      return value;
    }
    let formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });
    return formatter.format(value);
  });

  Vue.http.options.emulateJSON = true;
  Vue.http.options.emulateHTTP = true;
  Vue.http.options.root = base_server + 'api/';
  Vue.config.productionTip = false;

  if (environment !== 'local' && process.env.NODE_ENV === 'production') {
    Sentry.init({
      Vue,
      dsn: 'https://26cc6d9a67a3d4aa4ae008353f071d66@o4504123718565888.ingest.us.sentry.io/4511299150151680',
      sendDefaultPii: true,
      environment,
      release: version
    });
  }

  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount('#app');
}
